import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { InfoIcon, WarningIcon } from '@udacity/chakra-uds-icons';

import { useNewsletterSignUpHandler } from '~/hooks/use-newsletter-signup';

const newsletterFormSchema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Please enter a valid email address'),
});

type NewsletterFormData = z.infer<typeof newsletterFormSchema>;

export function NewsletterSignUp() {
  const { t } = useTranslation();
  const handleNewsletterSubmit = useNewsletterSignUpHandler();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterFormSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (data) => {
    await handleNewsletterSubmit(data);
    reset();
  });

  const submitButtonLabel = t('footer.newsletter.subscribeToNewsletter');

  return (
    <Box as='form' maxWidth={{ base: '100%', md: '780px' }} width='100%' onSubmit={onSubmit}>
      <FormControl isInvalid={Boolean(errors.email)}>
        <Flex flexDirection={{ base: 'column', md: 'row' }} gap={{ base: '16px', md: '8px' }}>
          <Flex flex={1} flexDirection='column' gap='8px'>
            <Flex justifyContent='space-between'>
              <FormLabel alignItems='center' display='flex'>
                {t('footer.newsletter.submitYourEmail')}
              </FormLabel>
              <Popover placement='top' strategy='fixed'>
                <PopoverTrigger>
                  <IconButton
                    aria-label='Sign up Terms and Conditions.'
                    icon={<InfoIcon color='black' height='24px' width='24px' />}
                    paddingInline='0px'
                    size='xs'
                    variant='ghost'
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverBody fontWeight='500' mt='16px'>
                    <Trans
                      i18nKey='footer.newsletter.terms'
                      values={{ submitButtonLabel }}
                      components={{
                        termsLink: <Box as={Link} href='/en-US/legal/terms-of-use' textDecoration='underline' />,
                        privacyLink: <Box as={Link} href='/legal/privacy' textDecoration='underline' />,
                      }}
                    />
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>

            <Flex flexDirection='column'>
              <Flex flexDirection={{ base: 'column', md: 'row' }} gap={2}>
                <Input
                  autoComplete='email'
                  height='48px'
                  placeholder={t('footer.newsletter.emailPlaceholder') ?? undefined}
                  type='email'
                  {...register('email')}
                />
                <Button type='submit' variant='outline'>
                  {submitButtonLabel}
                </Button>
              </Flex>
              {errors.email && (
                <FormErrorMessage mt='8px'>
                  <>
                    <WarningIcon h='26px' w='26px' />
                    {errors.email.message}
                  </>
                </FormErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
      </FormControl>
    </Box>
  );
}
